import React, { useState } from 'react';
import { AlertCircle, ChevronRight, Globe, Zap, Shield, Clock, CheckCircle, ArrowRight } from 'lucide-react';

const MAKE_WEBHOOK_URL = process.env.REACT_APP_MAKE_WEBHOOK_URL || 'https://hook.eu2.make.com/pvw36kfwo5bq7o3irg7cwy81x7gqwylg';

function App() {
  const [formData, setFormData] = useState({
    website: '',
    name: '',
    email: '',
    company: '',
    phone: '',
    consent: false
  });
  const [loading, setLoading] = useState(false);
  const [auditPhase, setAuditPhase] = useState('');
  const [progress, setProgress] = useState(0);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for website input
    if (name === 'website') {
      let processedUrl = value;
      // Only add https:// if it doesn't already have a protocol
      if (value && !value.match(/^https?:\/\//i)) {
        processedUrl = `https://${value}`;
      }
      setFormData(prev => ({
        ...prev,
        website: value // Store original input
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const phases = [
      'Analyserer nettsidens ytelse...',
      'Sjekker søkemotoroptimalisering...',
      'Undersøker teknisk oppsett...',
      'Vurderer mobiloptimalisering...',
      'Genererer SEO-rapport...'
    ];

    try {
      // Process the URL before sending
      let processedWebsite = formData.website;
      if (!processedWebsite.match(/^https?:\/\//i)) {
        processedWebsite = `https://${processedWebsite}`;
      }

      // Show progress animation
      for (let i = 0; i < phases.length; i++) {
        setAuditPhase(phases[i]);
        setProgress((i + 1) * 20);
        await new Promise(r => setTimeout(r, 800));
      }

      // Send to Make.com webhook
      const response = await fetch(MAKE_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          website: processedWebsite, // Send processed URL
          submittedAt: new Date().toISOString(),
        })
      });

      if (!response.ok) {
        throw new Error('Kunne ikke sende forespørselen');
      }

      setFormSubmitted(true);
    } catch (err) {
      setError('Det oppstod en feil. Vennligst prøv igjen.');
      console.error('Submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Hero Section */}
        <div className="text-center space-y-4 py-8">
          <h1 className="text-4xl font-bold text-gray-800">
            Få en Gratis SEO-Analyse av Din Nettside
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Få innsikt i hvordan du kan forbedre din nettsides synlighet og rangering i søkemotorene
          </p>
          
          <div className="flex justify-center flex-wrap gap-8 pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="text-[#7DF9FF]" />
              <span>100% Kostnadsfritt</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="text-[#7DF9FF]" />
              <span>Rask Analyse</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="text-[#7DF9FF]" />
              <span>Detaljert Rapport</span>
            </div>
          </div>
        </div>

        {/* Main Form Section */}
        <div className="space-y-6">
          <Card className="border-t-4" style={{ borderTopColor: '#7DF9FF' }}>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Nettside URL*</label>
                  <input
                    type="text"
                    name="website"
                    placeholder="f.eks. scandicommerce.no"
                    className="w-full p-3 border rounded focus:ring-2 focus:ring-[#7DF9FF] focus:border-transparent"
                    value={formData.website}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Navn*</label>
                  <input
                    type="text"
                    name="name"
                    className="w-full p-3 border rounded focus:ring-2 focus:ring-[#7DF9FF] focus:border-transparent"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">E-post*</label>
                  <input
                    type="email"
                    name="email"
                    className="w-full p-3 border rounded focus:ring-2 focus:ring-[#7DF9FF] focus:border-transparent"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Bedrift*</label>
                  <input
                    type="text"
                    name="company"
                    className="w-full p-3 border rounded focus:ring-2 focus:ring-[#7DF9FF] focus:border-transparent"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Telefon*</label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full p-3 border rounded focus:ring-2 focus:ring-[#7DF9FF] focus:border-transparent"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    name="consent"
                    id="consent"
                    className="mt-1"
                    checked={formData.consent}
                    onChange={handleInputChange}
                    required
                  />
                  <label htmlFor="consent" className="text-sm text-gray-600">
                    Jeg godtar å motta analyserapporten og relevante tips om nettoptimalisering fra ScandiCommerce AS*
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full p-3 bg-[#7DF9FF] text-gray-800 rounded hover:opacity-90 font-medium flex items-center justify-center gap-2 transition-all"
                >
                  {loading ? 'Analyserer...' : 'Få Din Gratis SEO-Rapport'} 
                  {loading ? <span className="animate-spin">⚡</span> : <ArrowRight size={16} />}
                </button>
              </form>
            </CardContent>
          </Card>

          {/* Progress Indicator */}
          {loading && (
            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span>{auditPhase}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#7DF9FF] transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <Alert className="border-red-500 bg-red-50 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </Alert>
          )}

          {/* Success Message */}
          {formSubmitted && (
            <Alert className="border-[#7DF9FF] bg-[#7DF9FF]/10">
              <CheckCircle className="h-4 w-4" />
              <div>
                <h3 className="font-medium">Din SEO-Rapport er på vei!</h3>
                <p>Vi analyserer nå din nettside og sender en detaljert rapport til din e-post innen få minutter.</p>
              </div>
            </Alert>
          )}
        </div>

        {/* Footer */}
        <div className="text-center py-8 text-gray-600 border-t mt-8">
          <p>ScandiCommerce AS © 2024</p>
        </div>
      </div>
    </div>
  );
}

// Component Definitions
const Card = ({ children, className = '', ...props }) => (
  <div className={`bg-white rounded-lg shadow ${className}`} {...props}>
    {children}
  </div>
);

const CardHeader = ({ children }) => (
  <div className="p-6 pb-0">{children}</div>
);

const CardContent = ({ children }) => (
  <div className="p-6">{children}</div>
);

const Alert = ({ children, className = '' }) => (
  <div className={`p-4 rounded-lg flex items-start gap-3 ${className}`}>
    {children}
  </div>
);

export default App;